import React, { useState, useContext } from "react";
import { UserContext } from "../context/UserContext"; // Import UserContext
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CategoryIcon from "@mui/icons-material/Category";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import axios from "axios"; // Import Axios
import API_BASE_URL from "../../src/config"; // Adjust as needed

const PostAd = () => {
  const { user } = useContext(UserContext); // Access user ID from UserContext
  const [ad, setAd] = useState({
    title: "",
    business: "",
    category: "",
    location: "",
    description: "",
    price: "",
    image_url: "",
  });

  const categories = [
    "Home & Living",
    "Business Support",
    "Travel & Logistics",
    "Event & Party Services",
    "Education & Learning",
    "Digital & Tech Services",
    "Special Services",
  ];

  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    setAd({ ...ad, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSuccessMessage("");
    setErrorMessage("");

    console.log("🛠 Data being sent to API:", ad); // Debugging Log

    // Check if all required fields are filled
    if (
      !ad.title ||
      !ad.business ||
      !ad.category ||
      !ad.location ||
      !ad.image_url ||
      !ad.price
    ) {
      console.error("❌ Missing required fields!", ad);
      setErrorMessage("❌ Please fill in all required fields.");
      setSubmitted(false);
      return;
    }

    // Make sure user is logged in and has a valid user_id
    if (!user || !user.id) {
      console.error("❌ User not logged in!");
      setErrorMessage("❌ User not logged in.");
      setSubmitted(false);
      return;
    }

    try {
      // Add user_id to the ad object
      const formattedAd = {
        ...ad,
        price: parseFloat(ad.price) || 0,
        user_id: user.id,
      };

      // Post request to backend with the user_id and ad data
       const response = await axios.post(
        `${API_BASE_URL}/sponsored-ads`,
        // `http://localhost:5000/api/sponsored-ads`,
        formattedAd
      );

      console.log("✅ Ad posted successfully:", response.data);
      setSuccessMessage(`✅ Ad Posted: ${response.data.title}`);

      // Reset form after successful submission
      setAd({
        title: "",
        business: "",
        category: "",
        location: "",
        description: "",
        price: "",
        image_url: "",
      });
    } catch (error) {
      console.error(
        "🔥 Error posting ad:",
        error.response ? error.response.data : error
      );
      setErrorMessage("❌ Failed to post ad. Please try again.");
    }
    setSubmitted(false);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 5 ,mb:4}}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography
           variant="h5"
           color = "#42326e" 
           fontWeight="bold"
           textAlign="center"
           mb={3}
          >
            📢 Post a Sponsored Ad
          </Typography>

          {/* Success & Error Messages */}
          {successMessage && (
            <Snackbar
              open
              autoHideDuration={4000}
              onClose={() => setSuccessMessage("")}
            >
              <Alert severity="success">{successMessage}</Alert>
            </Snackbar>
          )}
          {errorMessage && (
            <Snackbar
              open
              autoHideDuration={4000}
              onClose={() => setErrorMessage("")}
            >
              <Alert severity="error">{errorMessage}</Alert>
            </Snackbar>
          )}

          <form onSubmit={handleSubmit}>
            {/* Ad Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <TitleIcon sx={{ flexShrink: 0, color:"#42326e" }} />
              <TextField
                fullWidth
                label="Ad Title"
                variant="outlined"
                name="title"
                value={ad.title}
                onChange={handleChange}
                required
              />
            </Box>

            {/* Business Name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <StorefrontIcon sx={{ flexShrink: 0, color:"#42326e" }} />
              <TextField
                fullWidth
                label="Business Name"
                variant="outlined"
                name="business"
                value={ad.business}
                onChange={handleChange}
                required
              />
            </Box>

            {/* Category */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CategoryIcon sx={{ flexShrink: 0, color:"#42326e" }} />
              <TextField
                select
                fullWidth
                label="Category"
                variant="outlined"
                name="category"
                value={ad.category}
                onChange={handleChange}
                required
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Location */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LocationOnIcon sx={{ flexShrink: 0, color:"#42326e" }} />
              <TextField
                fullWidth
                label="Location"
                variant="outlined"
                name="location"
                value={ad.location}
                onChange={handleChange}
                required
              />
            </Box>

            {/* Description */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <DescriptionIcon sx={{ flexShrink: 0, color:"#42326e" }} />
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                name="description"
                value={ad.description}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Box>

            {/* Price */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon
                sx={{ flexShrink: 0, color:"#42326e" }}
              />
              <TextField
                fullWidth
                label="Price (₹)"
                variant="outlined"
                name="price"
                type="number"
                value={ad.price}
                onChange={handleChange}
                inputProps={{ min: 0, step: "0.01" }} // Restrict negative values
                required
              />
            </Box>

            {/* Image URL */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <ImageIcon sx={{ flexShrink: 0, color:"#42326e" }} />
              <TextField
                fullWidth
                label="Image URL"
                variant="outlined"
                name="image_url"
                value={ad.image_url}
                onChange={handleChange}
                required
              />
            </Box>

            {/* Image Preview */}
            {ad.image_url && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <img
                  src={ad.image_url}
                  alt="Ad Preview"
                  style={{
                    maxWidth: "100%",
                    height: "150px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}

            {/* Submit Button */}
            <Button
              variant="contained"
              color="success"
              fullWidth
               sx={{
                mt: 3,
                py:1,
                backgroundColor: "#42326e",
                color: "white",
                "&:hover": {
                  backgroundColor: "black",
                  color: "white",
                },
              }}
              type="submit"
              disabled={submitted}
            >
              {submitted ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Post Ad"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default PostAd;
